import { useTranslation } from "react-i18next";

import LanguageSwitcher from "@/components/LanguageSwitcher";
import LogoutButton from "@/components/LogoutButton";
import OnboardingWizard from "@/components/onboarding/OnboardingWizard";

/**
 * apps/web :: pages :: OnboardingPage
 *
 * Route-level wrapper around the onboarding wizard. Renders the page chrome
 * (eyebrow / title / subtitle) above `<OnboardingWizard />`. The wizard
 * owns all data-fetching, step state, and submit side-effects; this page
 * is deliberately thin.
 *
 * No TopNav or LearnerPortalShell here — the user hasn't finished onboarding
 * yet. A compact LanguageSwitcher + Sign Out button sit in the top-right so
 * users can pick their language before the native_language wizard step and
 * always retain an explicit escape hatch (ROADS-510).
 */
export default function OnboardingPage() {
  const { t, i18n } = useTranslation();

  return (
    <>
      <div className="flex items-center justify-end gap-sm px-md pt-sm">
        <LanguageSwitcher
          currentLang={i18n.language}
          onChange={(l) => void i18n.changeLanguage(l)}
          variant="compact"
        />
        <LogoutButton className="inline-flex items-center justify-center rounded-md border border-brand-primary/20 bg-white px-sm py-xs text-xs font-medium text-brand-primary transition-colors hover:bg-brand-primary/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary" />
      </div>
      <section className="mx-auto w-full max-w-3xl px-md pb-lg sm:px-lg">
        <header className="mb-lg">
          <p className="text-xs font-semibold uppercase tracking-wide text-neutral-600">
            {t("onboarding.eyebrow")}
          </p>
          <h1
            data-testid="onboarding-heading"
            className="mt-xs font-display text-3xl font-bold text-brand-primary"
          >
            {t("onboarding.title")}
          </h1>
          <p className="mt-xs text-sm text-neutral-700">
            {t("onboarding.subtitle")}
          </p>
        </header>

        <OnboardingWizard />
      </section>
    </>
  );
}
